'use client'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/select'

type Category = {
  _id: string
  title?: string | null
  slug?: string | null
}

type CategoryFilterProps = {
  categories: Category[]
  currentCategorySlug?: string | null
  basePath?: string // Base path for the filter (e.g., '/the-family-hub/food')
}

export function CategoryFilter({
  categories,
  currentCategorySlug,
  basePath = '/the-family-hub/',
}: CategoryFilterProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const query = searchParams.get('q') || ''
  const page = searchParams.get('page') || ''

  const handleCategoryChange = (value: string) => {
    const params = new URLSearchParams()
    if (query) params.set('q', query)
    // Use "all" as a special value to clear the category filter
    if (value && value !== 'all') params.set('category', value)
    // Reset to page 1 when changing category (don't preserve page param)
    const queryString = params.toString()
    const path = basePath.endsWith('/') ? basePath : `${basePath}/`
    router.push(`${path}${queryString ? `?${queryString}` : ''}`)
  }

  return (
    <div className="flex flex-col gap-3">
      <label htmlFor="category-filter" className="text-sm font-medium">
        Filter by Category
      </label>
      <Select
        value={currentCategorySlug || 'all'}
        onValueChange={handleCategoryChange}
      >
        <SelectTrigger id="category-filter" className="w-full md:w-[200px]">
          <SelectValue placeholder="All Categories" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Categories</SelectItem>
          {categories.map((category) => {
            const slug = category.slug || ''
            // Skip categories without slugs
            if (!slug) return null
            return (
              <SelectItem key={category._id} value={slug}>
                {category.title || category.slug}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}
